<template>
  <div :class="['tm-flag', flagTypeClass]">
    <img
      class="tm-flag__img"
      :src="placeholderImageUrl"
      :alt="countryPhone?.text"
    />
  </div>
</template>

<script lang="ts">
import { countryPhoneService } from '@widgets/shared/services/countryPhoneService'
import { computed, defineComponent, type PropType } from '@/composition/vue/compositionApi'
import { FlagType, getFlagProps } from '@/composition/flag'
import type { TmCountryCode } from '@/services/types'
import placeholderImageUrl from '@/assets/images/flags/flag_placeholder.png'
import type { StylesheetSizeProp, UrlWrappedAssetPath } from '@/types'
import TmAbsurdError from '@/core/error/tmAbsurdError'
import { getImageSetFromMetadata } from '@/utils/imageSet'
import flagOtherImageUrl from '@/assets/images/flags/flag_other.svg'
import flagsGeneralImageMetadata from '@/assets/images/flags/flags_general.png?w=22;44&basePixels=22&format=png;avif;webp&as=metadata'
import flagsExtendedImageMetadataImageMetadata from '@/assets/images/flags/flags_extended.png?w=22;44&basePixels=22&format=png;avif;webp&as=metadata'

export default defineComponent({
  props: {
    countryCode: {
      type: String as PropType<TmCountryCode>,
      required: true,
    },
  },
  setup(props) {
    const countryPhone = computed(() => countryPhoneService.getCountryPhone(props.countryCode))

    const flagProps = computed(() => getFlagProps(props.countryCode))
    const flagType = computed(() => flagProps.value.type)
    const flagTypeClass = computed(() => {
      switch (flagProps.value.type) {
        case FlagType.General:
          return 'tm-flag--general'
        case FlagType.Extended:
          return 'tm-flag--alt'
        case FlagType.Other:
          return 'tm-flag--other'
        default:
          throw new TmAbsurdError(flagProps.value.type)
      }
    })
    const flagPosition = computed<StylesheetSizeProp>(() => `${flagProps.value.position * 100}%`)

    return {
      placeholderImageUrl,
      countryPhone,
      flagType,
      flagTypeClass,
      flagPosition,
      flagOtherImageUrl: `url(${flagOtherImageUrl})` satisfies UrlWrappedAssetPath,
      generalFlagImages: getImageSetFromMetadata(flagsGeneralImageMetadata),
      altFlagImages: getImageSetFromMetadata(flagsExtendedImageMetadataImageMetadata),
    }
  },
})
</script>

<style lang="scss" scoped>
.tm-flag {
  flex: 0 auto;
  display: flex;
  align-items: center;
  width: 22px;
  height: 22px;

  &__img {
    width: 22px;
    height: 15px;
    border-radius: $border-radius-sm;
    box-shadow: 0 0 1px rgba($black, 0.25);
    background-size: 100%;
    background-position-y: v-bind(flagPosition);
  }
}

.tm-flag--other {
  .tm-flag__img {
    background-image: v-bind(flagOtherImageUrl);
  }
}

.tm-flag--general {
  .tm-flag__img {
    background-image: v-bind('generalFlagImages.fallback');

    @supports (background-image: image-set(url('img.avif') type('image/avif') 2x)) {
      background-image: v-bind('generalFlagImages.imageSet');
    }
  }
}

.tm-flag--alt {
  .tm-flag__img {
    background-image: v-bind('altFlagImages.fallback');

    @supports (background-image: image-set(url('img.avif') type('image/avif') 2x)) {
      background-image: v-bind('altFlagImages.imageSet');
    }
  }
}
</style>
